import { useMemo } from 'react'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { getColumn } from '../arrow/arqueroHelpers'

export interface SessionClusters {
  /** Distinct leiden cluster ids, ordered numerically when possible */
  clusters: string[]
  /** cellId list per cluster (cellIds are in the format PlotCellImage expects) */
  cellIdsByCluster: Map<string, string[]>
  /** Every cell id in the session (for the "All clusters" view) */
  allCellIds: string[]
  /** Total cells in the loaded session */
  totalCells: number
}

const EMPTY: SessionClusters = {
  clusters: [],
  cellIdsByCluster: new Map(),
  allCellIds: [],
  totalCells: 0,
}

/**
 * Groups the loaded session's cells by their Leiden cluster, reading the
 * `leiden` and `CELL_ID` columns straight from the already-loaded dataframe.
 */
export const useSessionClusters = (): SessionClusters => {
  const { dataFrame } = useCellVisualizationsSlice()

  return useMemo<SessionClusters>(() => {
    if (!dataFrame || dataFrame.numRows() === 0) return EMPTY

    const leiden = getColumn<string | number>(dataFrame, 'leiden')
    const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
    if (leiden.length === 0 || cellIds.length === 0) {
      return { ...EMPTY, allCellIds: cellIds, totalCells: cellIds.length }
    }

    const cellIdsByCluster = new Map<string, string[]>()
    for (let i = 0; i < cellIds.length; i += 1) {
      const value = leiden[i]
      const hasCluster = value !== undefined && value !== null && value !== ''
      if (hasCluster) {
        const key = String(value)
        const existing = cellIdsByCluster.get(key)
        if (existing) existing.push(cellIds[i])
        else cellIdsByCluster.set(key, [cellIds[i]])
      }
    }

    const clusters = Array.from(cellIdsByCluster.keys()).sort((a, b) => {
      const na = Number(a)
      const nb = Number(b)
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb
      return a.localeCompare(b)
    })

    return { clusters, cellIdsByCluster, allCellIds: cellIds, totalCells: cellIds.length }
  }, [dataFrame])
}

export default useSessionClusters
